import type { ReactNode } from "react";
import { LogOut, Dumbbell, SlidersHorizontal, ShieldCheck, Users, UserSearch } from "lucide-react";

export type ShellTab = "treino" | "aulas" | "alunos" | "automacao" | "admin";

const TAB_LABEL: Record<ShellTab, string> = {
  treino: "Treino",
  aulas: "Aulas",
  alunos: "Alunos",
  automacao: "Automação",
  admin: "Admin",
};

const TAB_ICON: Record<ShellTab, ReactNode> = {
  treino: <Dumbbell size={15} strokeWidth={2} />,
  aulas: <Users size={15} strokeWidth={2} />,
  alunos: <UserSearch size={15} strokeWidth={2} />,
  automacao: <SlidersHorizontal size={15} strokeWidth={2} />,
  admin: <ShieldCheck size={15} strokeWidth={2} />,
};

export interface ShellStatus {
  credential: { email: string; tokenExp: number | null; hasToken: boolean } | null;
  isAdmin: boolean;
}

export function AppShell({
  status,
  tab,
  onTab,
  onLogout,
  automationOn,
  children,
}: {
  status: ShellStatus;
  tab: ShellTab;
  onTab: (tab: ShellTab) => void;
  onLogout: () => void;
  /** Automation toggle state, surfaced as a badge on the tab. */
  automationOn: boolean;
  children: ReactNode;
}) {
  const tabs: ShellTab[] = status.isAdmin
    ? ["treino", "aulas", "alunos", "automacao", "admin"]
    : ["treino", "aulas", "automacao"];

  return (
    <div className="cb-shell">
      <header className="cb-topbar">
        <span className="cb-brand">
          CHECK<span style={{ color: "var(--cb-accent)" }}>BUN</span>
        </span>

        <nav className="cb-tabs cb-tabs--desktop" role="tablist" aria-label="Seções">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className="cb-tab"
              onClick={() => onTab(t)}
            >
              {TAB_ICON[t]}
              {TAB_LABEL[t]}
              {t === "automacao" && (
                <span
                  className="cb-led cb-led--live"
                  style={{ color: automationOn ? "var(--cb-success)" : "var(--cb-subtle)" }}
                />
              )}
            </button>
          ))}
        </nav>

        <div className="cb-spacer" />

        {status.credential && (
          <span className="cb-chip cb-chip--ghost" style={{ maxWidth: "13rem" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{status.credential.email}</span>
          </span>
        )}

        <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={onLogout}>
          <LogOut size={12} strokeWidth={2} />
          Sair
        </button>
      </header>

      <main className="cb-main">{children}</main>

      <nav className="cb-mobile-tabs" role="tablist" aria-label="Seções">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className="cb-mobile-tab"
            onClick={() => onTab(t)}
          >
            {TAB_ICON[t]}
            {TAB_LABEL[t]}
          </button>
        ))}
      </nav>
    </div>
  );
}
